import React from 'react';
import { useFlag } from '@unleash/proxy-client-react';
import * as RadixTooltip from '@radix-ui/react-tooltip';

import { Container, ImgWrapper, Image, Message, Messages, CustomRadixContent } from './styles';

import BadgeCautelar from '../../images/icons/badge_cautelar_aprovada.svg';
import BadgeInstaCarroCautelar from '../../images/icons/badge_instacarro_cautelar_aprovada.svg';
import BadgeLongarina from '../../images/icons/badge_longarina.svg';
import BadgeColunas from '../../images/icons/badge_colunas.svg';
import BadgeBomKm from '../../images/icons/badge_bom_km.svg';
import BadgeCambio from '../../images/icons/badge_cambio.svg';
import BadgeScanner from '../../images/icons/badge_scanner.svg';
import BadgeMargem from '../../images/icons/badge_margem.svg';
import BadgeRevenda from '../../images/icons/badge_revenda.svg';
import BadgeUberRJ from '../../images/icons/badge_uber_rj.svg';
import BadgeUberSP from '../../images/icons/badge_uber_sp.svg';
import BadgeUberBoothStates from '../../images/icons/badge_uber.svg';
import BadgePneus from '../../images/icons/badge_pneus.svg'; // Novo ícone para pneus
import BadgeInterior from '../../images/icons/badge_interior.svg'; // Novo ícone para conservação do interior
import BadgeGNV from '../../images/icons/badge_gnv.svg'; // Novo ícone para GNV
import BadgeBlindagem from '../../images/icons/badge_blindagem.svg'; // Novo ícone para blindagem
import BadgeMotor from '../../images/icons/badge_motor.svg'; // Badge de motor sem apontamentos
import PlusBadges from '../../images/icons/plus_badges.svg';

function Tooltip({ message, children }) {
  return (
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>

      <CustomRadixContent side="top" align="center">
        {message}
        <RadixTooltip.Arrow />
      </CustomRadixContent>
    </RadixTooltip.Root>
  );
}

const EvaluationBadges = ({ auction }) => {
  const flagBadgeCautelar = useFlag('badge-cautelar');
  const flagBadgeLongarina = useFlag('badge-longarina');
  const flagBadgeColunas = useFlag('badge-colunas');
  const flagBadgeKm = useFlag('badge-km');
  const flagBadgeCambio = useFlag('badge-cambio');
  const flagBadgeScanner = useFlag('badge-scanner');
  const flagBadgeMargem = useFlag('badge-margem');
  const flagBadgeRevenda = useFlag('badge-giro');
  const flagBadgeUberSP = useFlag('badge-uber-sp');
  const flagBadgeUberRJ = useFlag('badge-uber-rj');
  const flagBadgeUberBoothStates = useFlag('badge-uber');
  const flagBadgePneus = useFlag('badge-pneus'); // Nova flag para badge de pneus
  const flagBadgeInterior = useFlag('badge-interior'); // Nova flag para badge de conservação do interior
  const flagBadgeGNV = useFlag('badge-gnv'); // Nova flag para badge de GNV
  const flagBadgeBlindagem = useFlag('badge-blindagem');

  // Para D2D, os dados do vehicle podem estar em auction?.vehicle (nível raiz) ou em summary
  // Usar vehicle primeiro, depois summary como fallback
  const vehicle = auction?.vehicle;
  const vehicleData = vehicle || auction?.summary || {};
  const isD2D = auction?.summary?.hasD2D || false;

  // Para D2D, verificar também o status da cautelar em summary.additionalInfo
  const hasPrecautionaryReport =
    vehicleData?.precautionaryReport ||
    auction?.summary?.precautionaryReport ||
    (auction?.summary?.additionalInfo?.precautionaryStatus &&
      String(auction.summary.additionalInfo.precautionaryStatus)
        .toLowerCase()
        .includes('aprovad'));

  const ICONS = {
    BadgeCautelar: isD2D ? BadgeInstaCarroCautelar : BadgeCautelar,
    BadgeLongarina,
    BadgeColunas,
    BadgeBomKm,
    BadgeCambio,
    BadgeScanner,
    BadgeMargem,
    BadgeRevenda,
    BadgeUberSP,
    BadgeUberRJ,
    BadgeUberBoothStates,
    BadgePneus, // Nova badge de pneus
    BadgeInterior, // Nova badge de conservação do interior
    BadgeGNV, // Nova badge de GNV
    BadgeBlindagem, // Nova badge de blindagem
    BadgeMotor, // Badge de motor sem apontamentos
    PlusBadges,
  };

  // Para D2D, ignorar flags do Unleash - mostrar badges baseado apenas nos dados
  const shouldShowUberBoothStates = isD2D
    ? (vehicleData?.canHaveUberSPBadge || auction?.summary?.canHaveUberSPBadge) &&
      (vehicleData?.canHaveUberRJBadge || auction?.summary?.canHaveUberRJBadge)
    : (vehicleData?.canHaveUberSPBadge || auction?.summary?.canHaveUberSPBadge) &&
      (vehicleData?.canHaveUberRJBadge || auction?.summary?.canHaveUberRJBadge) &&
      flagBadgeUberBoothStates;

  const shouldShowUberSP = isD2D
    ? (vehicleData?.canHaveUberSPBadge || auction?.summary?.canHaveUberSPBadge) &&
      !shouldShowUberBoothStates
    : (vehicleData?.canHaveUberSPBadge || auction?.summary?.canHaveUberSPBadge) &&
      flagBadgeUberSP &&
      !shouldShowUberBoothStates;

  const shouldShowUberRJ = isD2D
    ? (vehicleData?.canHaveUberRJBadge || auction?.summary?.canHaveUberRJBadge) &&
      !shouldShowUberBoothStates
    : (vehicleData?.canHaveUberRJBadge || auction?.summary?.canHaveUberRJBadge) &&
      flagBadgeUberRJ &&
      !shouldShowUberBoothStates;

  const shouldShowCautelar = isD2D
    ? hasPrecautionaryReport
    : hasPrecautionaryReport && flagBadgeCautelar;

  const shouldShowLongarina = isD2D
    ? (vehicleData?.canHaveCarSparBadge || auction?.summary?.canHaveCarSparBadge) &&
      !shouldShowCautelar
    : (vehicleData?.canHaveCarSparBadge || auction?.summary?.canHaveCarSparBadge) &&
      flagBadgeLongarina &&
      !shouldShowCautelar;

  const shouldShowColunas = isD2D
    ? (vehicleData?.canHaveColumnBadge || auction?.summary?.canHaveColumnBadge) &&
      !shouldShowCautelar
    : (vehicleData?.canHaveColumnBadge || auction?.summary?.canHaveColumnBadge) &&
      flagBadgeColunas &&
      !shouldShowCautelar;

  // Nova lógica para badge de pneus
  const shouldShowPneus = isD2D
    ? vehicleData?.canHaveTiresBadge || auction?.summary?.canHaveTiresBadge
    : (vehicleData?.canHaveTiresBadge || auction?.summary?.canHaveTiresBadge) && flagBadgePneus;

  // Nova lógica para badge de conservação do interior
  const shouldShowInterior = isD2D
    ? vehicleData?.canHaveInteriorBadge || auction?.summary?.canHaveInteriorBadge
    : (vehicleData?.canHaveInteriorBadge || auction?.summary?.canHaveInteriorBadge) &&
      flagBadgeInterior;

  // Nova lógica para badge de GNV
  // Para D2D, também verificar se summary ou version contém "GNV"
  const hasGNVInSummary =
    (auction?.summary?.summary &&
      String(auction.summary.summary)
        .toUpperCase()
        .includes('GNV')) ||
    (auction?.summary?.version &&
      String(auction.summary.version)
        .toUpperCase()
        .includes('GNV')) ||
    (vehicleData?.summary &&
      String(vehicleData.summary)
        .toUpperCase()
        .includes('GNV')) ||
    (vehicleData?.version &&
      String(vehicleData.version)
        .toUpperCase()
        .includes('GNV'));

  const shouldShowGNV = isD2D
    ? vehicleData?.canHaveGNVBadge || auction?.summary?.canHaveGNVBadge || hasGNVInSummary
    : (vehicleData?.canHaveGNVBadge || auction?.summary?.canHaveGNVBadge) && flagBadgeGNV;

  const shouldShowBlindagem = isD2D
    ? vehicleData?.canHaveBlindagemBadge || auction?.summary?.canHaveBlindagemBadge
    : (vehicleData?.canHaveBlindagemBadge || auction?.summary?.canHaveBlindagemBadge) &&
      flagBadgeBlindagem;

  // Lógica para badge de motor sem apontamentos
  const shouldShowMotor = isD2D
    ? vehicleData?.canHaveEngineBadge || auction?.summary?.canHaveEngineBadge
    : vehicleData?.canHaveEngineBadge || auction?.summary?.canHaveEngineBadge;

  const MESSAGES = isHidden => ({
    BadgeCautelar: (
      <Message isHidden={isHidden}>
        {isD2D ? (
          <>
            <strong>Cautelar aprovada</strong> Aprovada, pode conter apontamentos
          </>
        ) : (
          <>
            <strong>Cautelar aprovada</strong> Cautelar Visão total verificada e aprovada
          </>
        )}
      </Message>
    ),
    BadgeLongarina: (
      <Message isHidden={isHidden}>
        <strong>Longarina sem apontamentos</strong> Verificada e sem observações
      </Message>
    ),
    BadgeColunas: (
      <Message isHidden={isHidden}>
        <strong>Colunas sem apontamentos</strong> Verificadas e sem observações
      </Message>
    ),
    BadgeBomKm: (
      <Message isHidden={isHidden}>
        <strong>Boa km por ano </strong> Na média de km para carros <br /> do mesmo ano
      </Message>
    ),
    BadgeCambio: (
      <Message isHidden={isHidden}>
        <strong>Câmbio sem apontamentos</strong> Teste inerte sem ressalvas
      </Message>
    ),
    BadgeScanner: (
      <Message isHidden={isHidden}>
        <strong>Scanner sem apontamentos</strong> Verificado e sem observações
      </Message>
    ),
    BadgeMargem: (
      <Message isHidden={isHidden}>
        <strong>Margem alta na revenda</strong> 70% abaixo do preço da web
      </Message>
    ),
    BadgeRevenda: (
      <Message isHidden={isHidden}>
        <strong>Giro rápido</strong> Mais vendidos segundo a Fenabrave
      </Message>
    ),
    BadgeUberSP: (
      <Message isHidden={isHidden}>
        <strong>Aceito no Uber Black SP e outros</strong> Modelo autorizado para Uber Black <br />
        em São Paulo, Brasília, Curitiba, Belo Horizonte e Porto Alegre
      </Message>
    ),
    BadgeUberRJ: (
      <Message isHidden={isHidden}>
        <strong>Aceito no Uber Black RJ e outros</strong> Modelo autorizado para Uber Black <br />
        no Rio de Janeiro, Campinas, Salvador, Recife, <br /> Florianópolis, Goiânia, Fortaleza e
        Vitória
      </Message>
    ),
    BadgeUberBoothStates: (
      <Message isHidden={isHidden}>
        <strong>Aceito no Uber Black SP, RJ e outros</strong> Modelo autorizado para Uber Black{' '}
        <br /> em São Paulo, Brasília, Curitiba, Belo Horizonte, <br />
        Porto Alegre, Rio de Janeiro, Salvador, Recife, Florianópolis, <br />
        Goiânia, Fortaleza e Vitória
      </Message>
    ),
    BadgePneus: (
      <Message isHidden={isHidden}>
        <strong>Pneus em condição de rodagem</strong>
      </Message>
    ),
    BadgeInterior: (
      <Message isHidden={isHidden}>
        <strong>Bom estado de conservação interior</strong>
      </Message>
    ),
    BadgeGNV: (
      <Message isHidden={isHidden}>
        <strong>Equipado com GNV</strong>
      </Message>
    ),
    BadgeBlindagem: (
      <Message isHidden={isHidden}>
        <strong>Veículo blindado</strong> Blindagem documentada
      </Message>
    ),
    BadgeMotor: (
      <Message isHidden={isHidden}>
        <strong>Motor sem apontamentos</strong> Teste inerte sem ressalvas
      </Message>
    ),
  });

  // Para D2D, apenas essas badges são permitidas:
  // - badgeCautelar (precautionaryReport)
  // - badgeCambioSemApontamento (canHaveTransmissionBadge)
  // - badgeGNV (canHaveGNVBadge)
  // - badgeBlindagem (canHaveBlindagemBadge)
  const ICONS_STATUS = {
    BadgeCautelar: shouldShowCautelar,
    BadgeLongarina: isD2D ? false : shouldShowLongarina, // Desabilitado para D2D
    BadgeColunas: isD2D ? false : shouldShowColunas, // Desabilitado para D2D
    BadgeBomKm: isD2D
      ? false // Desabilitado para D2D
      : (vehicleData?.kilometrageAcceptable || auction?.summary?.kilometrageAcceptable) &&
        flagBadgeKm,
    BadgeCambio: isD2D
      ? vehicleData?.canHaveTransmissionBadge || auction?.summary?.canHaveTransmissionBadge
      : (vehicleData?.canHaveTransmissionBadge || auction?.summary?.canHaveTransmissionBadge) &&
        flagBadgeCambio,
    BadgeScanner: isD2D
      ? false // Desabilitado para D2D (badgeMecanica pode ser apenas cambio)
      : (vehicleData?.canHaveScannerBadge || auction?.summary?.canHaveScannerBadge) &&
        flagBadgeScanner,
    BadgeMargem: isD2D
      ? false // Desabilitado para D2D
      : (vehicleData?.isBelowThreshold || auction?.summary?.isBelowThreshold) && flagBadgeMargem,
    BadgeRevenda: isD2D
      ? false // Desabilitado para D2D
      : (vehicleData?.fastSpin || auction?.summary?.fastSpin) && flagBadgeRevenda,
    BadgeUberSP: isD2D ? false : shouldShowUberSP, // Desabilitado para D2D
    BadgeUberRJ: isD2D ? false : shouldShowUberRJ, // Desabilitado para D2D
    BadgeUberBoothStates: isD2D ? false : shouldShowUberBoothStates, // Desabilitado para D2D
    BadgePneus: isD2D ? false : shouldShowPneus, // Desabilitado para D2D
    BadgeInterior: isD2D ? false : shouldShowInterior, // Desabilitado para D2D
    BadgeGNV: shouldShowGNV,
    BadgeBlindagem: shouldShowBlindagem,
    BadgeMotor: shouldShowMotor,
  };

  const getTrueIcons = () => {
    // Ordem de relevância conforme documento:
    // 1. ESTRUTURA: Cautelar, Longarina, Colunas, Boa km
    // 2. MECÂNICA: Motor, Câmbio, Scanner
    // 3. VISUAL: Interior
    // 4. EQUIPAMENTOS: Pneus, GNV
    // 5. PESQUISA: Blindagem
    // 6. MARGEM: Margem alta, Giro rápido
    // UBER: SP+RJ+outros, SP+outros, RJ+outros
    const badgeOrder = [
      // 1. ESTRUTURA
      'BadgeCautelar',
      'BadgeLongarina',
      'BadgeColunas',
      'BadgeBomKm',
      // 2. MECÂNICA
      'BadgeMotor',
      'BadgeCambio',
      'BadgeScanner',
      // 3. VISUAL
      'BadgeInterior',
      // 4. EQUIPAMENTOS
      'BadgePneus',
      'BadgeGNV',
      // 5. PESQUISA
      'BadgeBlindagem',
      // 6. MARGEM
      'BadgeMargem',
      'BadgeRevenda',
      // UBER
      'BadgeUberBoothStates',
      'BadgeUberSP',
      'BadgeUberRJ',
    ];

    const trueIcons = Object.keys(ICONS_STATUS).filter(key => ICONS_STATUS[key]);

    // Ordenar badges pela ordem de prioridade definida
    const orderedIcons = badgeOrder.filter(badge => trueIcons.includes(badge));
    const remainingIcons = trueIcons.filter(badge => !badgeOrder.includes(badge));
    const finalIcons = [...orderedIcons, ...remainingIcons];

    if (finalIcons.length > 3) {
      const visibleIcons = finalIcons.slice(0, 3);
      const hiddenIcons = finalIcons.slice(3);

      return {
        ...visibleIcons.reduce((result, key) => {
          result[key] = {
            icon: ICONS[key],
            message: MESSAGES(false)[key],
          };
          return result;
        }, {}),
        Plus: {
          icon: ICONS.PlusBadges,
          message: (
            <Messages>
              {hiddenIcons.map((key, index) => (
                <div key={index}>{MESSAGES(true)[key]}</div>
              ))}
            </Messages>
          ),
        },
      };
    }

    return finalIcons.reduce((result, key) => {
      result[key] = {
        icon: ICONS[key],
        message: MESSAGES(false)[key],
      };
      return result;
    }, {});
  };

  const iconsToDisplay = getTrueIcons();

  return (
    <Container>
      {Object.entries(iconsToDisplay).map(([key, { icon, message }]) => (
        <Tooltip key={key} message={message}>
          <ImgWrapper>
            <Image src={icon} alt={`Badge ${key}`} />
          </ImgWrapper>
        </Tooltip>
      ))}
    </Container>
  );
};

export default EvaluationBadges;
